import { useEffect, useState } from "react";
import { api, notify } from "./api";
type Member = {
  id: string;
  name: string;
  role: string;
  revokedAt: number | null;
};
type Invite = {
  id: string;
  role: string;
  expiresAt: number;
  revokedAt: number | null;
};
export function AccessManagement({ roomId }: { roomId: string }) {
  const [members, setMembers] = useState<Member[]>([]),
    [invites, setInvites] = useState<Invite[]>([]),
    [busy, setBusy] = useState(false);
  async function refresh() {
    const [m, i] = await Promise.all([
      api<Member[]>(`/api/rooms/${roomId}/members`),
      api<Invite[]>(`/api/rooms/${roomId}/invites`),
    ]);
    setMembers(m);
    setInvites(i);
  }
  useEffect(() => {
    void refresh().catch((e) => notify(e.message));
  }, [roomId]);
  async function change(path: string, body: unknown) {
    setBusy(true);
    try {
      await api(`/api/rooms/${roomId}/${path}`, body);
      await refresh();
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="access-management">
      <h3>Project access</h3>
      <p>
        Invitation links expire on the date chosen when created. Expiry stops
        new joins; existing members keep access. Removing a member closes their
        connections.
      </p>
      {members.map((m) => (
        <div className="access-row" key={m.id}>
          <span>{m.name}</span>
          {m.role === "owner" ? (
            <span>Owner</span>
          ) : (
            <select
              aria-label={`Access for ${m.name}`}
              disabled={busy}
              value={m.revokedAt ? "removed" : m.role}
              onChange={(e) =>
                void change(`members/${m.id}`, {
                  role: e.target.value === "removed" ? null : e.target.value,
                })
              }
            >
              <option value="editor">Editor</option>
              <option value="commenter">Commenter</option>
              <option value="viewer">Viewer</option>
              <option value="removed">Removed</option>
            </select>
          )}
        </div>
      ))}
      <details
        onToggle={(e) => {
          if (e.currentTarget.open)
            void refresh().catch((e) => notify(e.message));
        }}
      >
        <summary>Invitation links</summary>
        {invites
          .filter((i) => !i.revokedAt && i.expiresAt > Date.now())
          .map((i) => (
            <div className="access-row" key={i.id}>
              <span>
                {i.role} · until {new Date(i.expiresAt).toLocaleDateString()}
              </span>
              <button
                disabled={busy}
                onClick={() => void change(`invites/${i.id}/revoke`, {})}
              >
                Revoke link
              </button>
            </div>
          ))}
      </details>
    </section>
  );
}
